// src/utils/consultSteps.js
//
// The four steps of the doctor's consultation (Assessment, Prescription,
// Orders & follow-up, Review & sign): their order, the ?step= deep link, which
// are complete, and the Review checklist. Pure — the components only render it.
//
// Autosave means no step is forced: the stepper lets the doctor open any step
// in any order, and `done` is only a progress hint. The one hard rule is that
// signing needs a diagnosis (canSign).

export const STEPS = [
  { key: 'assessment', label: 'Assessment', short: 'Assess' },
  { key: 'prescription', label: 'Prescription', short: 'Rx' },
  { key: 'orders', label: 'Orders & follow-up', short: 'Orders' },
  { key: 'review', label: 'Review & sign', short: 'Sign' },
];
export const STEP_KEYS = STEPS.map(s => s.key);
export const DEFAULT_STEP = 'assessment';

export const isStepKey = k => STEP_KEYS.includes(k);
export const stepIndex = k => Math.max(0, STEP_KEYS.indexOf(k));

/** `?step=` from a location.search string (or URLSearchParams) → a valid key, else the default. */
export function parseStepParam(search) {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  const v = String(params.get('step') || '').toLowerCase();
  return isStepKey(v) ? v : DEFAULT_STEP;
}

/** A new search string with `step` set, every other param kept. */
export function withStepParam(search, key) {
  const params = new URLSearchParams(search || '');
  params.set('step', isStepKey(key) ? key : DEFAULT_STEP);
  return `?${params.toString()}`;
}

export const nextStepKey = k => STEP_KEYS[Math.min(stepIndex(k) + 1, STEP_KEYS.length - 1)];
export const prevStepKey = k => STEP_KEYS[Math.max(stepIndex(k) - 1, 0)];

const has = v => typeof v === 'string' && v.trim() !== '';

/** Signing needs a diagnosis, nothing else. */
export const canSign = form => has(form?.diagnosisText);

/** Number of next steps (instructions + lab suggestion) with real text. */
const stepCount = form => (form?.nextSteps || []).filter(s => has(s?.description)).length;
const hasFollowUp = form => form?.followUpWeeks !== '' && form?.followUpWeeks != null && Number(form.followUpWeeks) > 0;

/** Whether anything has been added on the Orders step. */
export const ordersTouched = form => stepCount(form) > 0 || hasFollowUp(form);

/**
 * Per-step state for the stepper.
 *  assessment  : 'done' when the diagnosis is non-blank, else 'todo'
 *  prescription: 'done' with >=1 medicine or the explicit "No medicines needed", else 'todo'
 *  orders      : always optional; 'done' once anything is added, else 'optional'
 *  review      : 'done' only once signed, else 'todo'
 */
export function stepStates({ form, medicineCount = 0, noMedicines = false, signed = false }) {
  return {
    assessment: canSign(form) ? 'done' : 'todo',
    prescription: medicineCount > 0 || noMedicines ? 'done' : 'todo',
    orders: ordersTouched(form) ? 'done' : 'optional',
    review: signed ? 'done' : 'todo',
  };
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The small line under each step name in the stepper. */
export function stepSubtitles({ form, medicineCount = 0, noMedicines = false, signed = false }) {
  const n = stepCount(form);
  const weeks = hasFollowUp(form) ? `${Number(form.followUpWeeks)} wks` : '';
  const orders = [n ? plural(n, 'step') : '', weeks].filter(Boolean).join(' · ');
  return {
    assessment: canSign(form) ? (has(form?.adviceForPatient) ? 'Diagnosis · advice' : 'Diagnosis') : 'Diagnosis needed',
    prescription: medicineCount > 0 ? plural(medicineCount, 'medicine') : noMedicines ? 'No medicines' : 'Not started',
    orders: orders || 'Optional',
    review: signed ? 'Signed' : '',
  };
}

const oneLine = (s, n = 110) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n).trimEnd()}…` : t;
};

/**
 * Review checklist rows. status: 'ok' (✓) | 'todo' (!) | 'optional' (–) | 'warn' (!).
 * `patientSees` marks sections the patient will be able to read once signed.
 * The "Check before signing" row is present only when allergies are unavailable
 * (`allergies === null`) or there are unchecked urgent open items.
 */
export function reviewRows({
  form, medicineCount = 0, medicineSummary = '', noMedicines = false, allergies, uncheckedUrgent = 0, orderSummary = '',
}) {
  const rows = [];
  const dx = canSign(form);
  const codes = has(form?.diagnosisCodes) ? ` · ${form.diagnosisCodes.trim()}` : '';
  rows.push({
    key: 'diagnosis', step: 'assessment', title: 'Diagnosis', status: dx ? 'ok' : 'todo', patientSees: true,
    summary: dx ? `${oneLine(form.diagnosisText)}${codes}` : 'Required — add a diagnosis to enable signing',
  });
  const advice = has(form?.adviceForPatient);
  rows.push({
    key: 'advice', step: 'assessment', title: 'Advice', status: advice ? 'ok' : 'optional', patientSees: true,
    summary: advice ? oneLine(form.adviceForPatient) : 'No advice written — optional',
  });
  const rxDone = medicineCount > 0 || noMedicines;
  rows.push({
    key: 'prescription', step: 'prescription',
    title: medicineCount > 0 ? `Prescription · ${plural(medicineCount, 'medicine')}` : 'Prescription',
    status: rxDone ? 'ok' : 'todo', patientSees: medicineCount > 0,
    summary: medicineCount > 0 ? oneLine(medicineSummary) : noMedicines ? 'No medicines needed' : 'Not done — add medicines or mark "No medicines needed"',
  });
  const orders = ordersTouched(form);
  rows.push({
    key: 'orders', step: 'orders', title: 'Orders & follow-up', status: orders ? 'ok' : 'optional', patientSees: orders,
    summary: orders ? oneLine(orderSummary) : 'Nothing added — optional',
  });
  const notes = [];
  if (allergies === null) notes.push('Allergies are not available — check the patient record before prescribing.');
  if (uncheckedUrgent > 0) notes.push(`${plural(uncheckedUrgent, 'urgent open item')} still unchecked.`);
  if (notes.length) {
    rows.push({ key: 'check', step: null, title: 'Check before signing', status: 'warn', patientSees: false, summary: notes.join(' ') });
  }
  return rows;
}

/** One line for the Orders row of the review: "HbA1c, UACR · 2 instructions · follow-up in 4 weeks". */
export function orderSummaryText({ tests = [], instructionCount = 0, followUpWeeks }) {
  const parts = [];
  if (tests.length) parts.push(tests.join(', '));
  if (instructionCount) parts.push(plural(instructionCount, 'instruction'));
  if (followUpWeeks !== '' && followUpWeeks != null && Number(followUpWeeks) > 0) parts.push(`follow-up in ${Number(followUpWeeks)} weeks`);
  return parts.join(' · ');
}

/** What signing will do for the chosen follow-up (changes with the week count). */
export function followUpSentence(value) {
  const weeks = value === '' || value == null ? 0 : Number(value);
  if (!weeks) return 'No follow-up item will be added when you sign.';
  return `Signing adds a “Book follow-up appointment (in ${weeks} week${weeks === 1 ? '' : 's'})” item for the care team; it doesn't create the appointment.`;
}
